import React, { useEffect, useMemo } from "react";
import { Slide, ToastContainer, toast } from "react-toastify";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material";

import { notificationAtom } from "@/src/state/notificationAtom";

import CustomToast from "@/src/components/molecules/CustomToast/CustomToast";

import { NOTIFICATION_SHOW_TIME_IN_MS } from "@/src/config/baanaConfig";

import {
  buildNotificationThemeStyles,
  toastIconColor
} from "./notificationContainer.styles";
import { Notification } from "./types";

import "./NotificationContainer.scss";

import { useAtom } from "jotai";

const AccessibleCloseButton = ({ closeToast }: { closeToast?: () => void }) => (
  <button
    aria-label="Close notification"
    onClick={closeToast}
    className="Toastify__close-button"
    type="button"
  >
    ×
  </button>
);

const NotificationContainer = () => {
  const [notification, setNotification] = useAtom<Notification | null>(
    notificationAtom
  );

  const theme = useTheme();

  useEffect(() => {
    if (notification) {
      // Stable ID prevents duplicate toasts
      const toastId = `${notification.variant}-${notification.text}-${
        notification.auctionId ?? ""
      }`;
      const hasLink =
        notification.auctionId !== undefined ||
        notification.linkText !== undefined;

      toast(<CustomToast notification={notification} />, {
        toastId,
        type: notification.variant,
        autoClose: notification.isAutoHideDisabled
          ? false
          : NOTIFICATION_SHOW_TIME_IN_MS,
        className: hasLink ? "toast-with-link" : undefined,
        closeButton: AccessibleCloseButton
      });

      // Clear the atom so it doesn't hold stale state,
      // but we need to be careful if the atom is used for other things.
      // Based on analysis, it is used as a trigger.
      setNotification(null);
    }
  }, [notification, setNotification]);

  const themeStyles = useMemo(
    () => buildNotificationThemeStyles(theme),
    [theme]
  );

  // Custom icon renderer using Material UI outlined icons
  const renderIcon = ({ type }: { type: string }) => {
    const iconProps = {
      sx: {
        fontSize: theme.typography.subtitle1.fontSize,
        color: toastIconColor(theme, type)
      }
    };

    switch (type) {
      case "success":
        return <CheckCircleOutlineIcon {...iconProps} />;
      case "error":
        return <ErrorOutlineIcon {...iconProps} />;
      case "warning":
        return <WarningAmberIcon {...iconProps} />;
      case "info":
        return <InfoOutlinedIcon {...iconProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <style>{themeStyles}</style>
      <ToastContainer
        position="top-right"
        autoClose={NOTIFICATION_SHOW_TIME_IN_MS}
        hideProgressBar={false}
        newestOnTop={true} // Show newest notifications on top for better UX
        closeOnClick={false} // "Mobile: onclick stops the progress bar, clicks away let it continue" -> If true, it closes. If false, it just handles events.
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={5} // "Stackable notifications (limit 5)"
        theme="colored" // Use colored variant to match "filled" variant from MUI Alert
        transition={Slide}
        icon={renderIcon} // Use Material UI icons instead of default react-toastify icons
        closeButton={AccessibleCloseButton}
      />
    </>
  );
};

export default NotificationContainer;
